<template>
  <div class="roles-permissions-container">
    <Sidebar />
    <div class="content-area">
      <div class="header d-flex justify-content-between align-items-center mb-4">
        <h2 class="title">Roles & Permissions Management</h2>
      </div>
      
      <div v-if="loading" class="loading-spinner d-flex justify-content-center my-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      
      <div v-else-if="error" class="alert alert-danger" role="alert">
        <p>{{ error }}</p>
      </div>
      
      <div v-else>
        <div class="mb-5">
          <PermissionsTable />
        </div>

        <div class="role-cards-container">
          <h3 class="section-title mb-4">Role Details</h3>
          <div class="row">
            <div v-for="role in roles" :key="role.role_id" class="col-md-6 col-lg-4 mb-4">
              <div class="card h-100 role-card">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <h5 class="card-title mb-0">{{ role.display_name || role.role_name }}</h5>
                  <span class="badge bg-primary">{{ role.permissions.length }} permissions</span>
                </div>
                <div class="card-body">
                  <p class="card-text text-muted">{{ role.description || 'No description available' }}</p>
                  
                  <div v-if="role.permissions.length > 0" class="permissions-section">
                    <h6 class="permissions-title">
                      <span>Permissions</span>
                      <button 
                        class="btn btn-sm btn-link p-0 ms-2" 
                        @click="togglePermissions(role.role_id)"
                      >
                        <i class="fas" :class="isExpanded(role.role_id) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                      </button>
                    </h6>
                    
                    <div v-if="isExpanded(role.role_id)" class="permissions-list">
                      <div v-for="permission in role.permissions" :key="permission.id" class="permission-item">
                        <span class="permission-name">{{ permission.display_name || permission.name }}</span>
                        <span class="permission-description">{{ permission.description }}</span>
                      </div>
                    </div>
                    <div v-else class="permissions-summary">
                      <div v-if="role.permissions.length > 3" class="permission-preview">
                        <span v-for="permission in role.permissions.slice(0, 3)" :key="permission.id" class="permission-chip">
                          {{ permission.display_name || permission.name }}
                        </span>
                        <span class="more-indicator">+{{ role.permissions.length - 3 }} more</span>
                      </div>
                      <div v-else class="permission-preview">
                        <span v-for="permission in role.permissions" :key="permission.id" class="permission-chip">
                          {{ permission.display_name || permission.name }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-permissions-message">
                    No permissions assigned to this role
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import Sidebar from './Sidebar.vue';
import PermissionsTable from './PermissionsTable.vue';
import apiService from '@/services/api';

export default {
  name: 'RolesAndPermissions',
  components: {
    Sidebar,
    PermissionsTable
  },
  setup() {
    const roles = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const expandedRoles = ref([]);
    
    const fetchRolesAndPermissions = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const response = await apiService.roles.getAllWithPermissions();
        
        if (response.data && response.data.success) {
          roles.value = response.data.data;
        } else {
          error.value = 'Failed to load roles and permissions: Invalid response format';
        }
      } catch (err) {
        console.error('Error fetching roles and permissions:', err);
        error.value = `Failed to load roles and permissions: ${err.message || 'Unknown error'}`;
      } finally {
        loading.value = false;
      }
    };
    
    const togglePermissions = (roleId) => {
      if (expandedRoles.value.includes(roleId)) {
        expandedRoles.value = expandedRoles.value.filter(id => id !== roleId);
      } else {
        expandedRoles.value.push(roleId);
      }
    };
    
    const isExpanded = (roleId) => {
      return expandedRoles.value.includes(roleId);
    };
    
    onMounted(() => {
      fetchRolesAndPermissions();
    });
    
    return {
      roles,
      loading,
      error,
      togglePermissions,
      isExpanded
    };
  }
};
</script>

<style scoped>
.roles-permissions-container {
  display: flex;
  width: 100%;
}

.content-area {
  flex: 1;
  padding: 2rem;
  background-color: #f8f9fa;
  min-height: 100vh;
  margin-left: 250px;
}

.title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #333;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.role-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075);
  transition: transform 0.2s, box-shadow 0.2s;
  border-radius: 0.5rem;
  overflow: hidden;
  border: none;
}

.role-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0,0,0,0.1);
}

.card-header {
  background-color: #fff;
  border-bottom: 1px solid rgba(0,0,0,0.1);
  padding: 1rem;
}

.card-title {
  font-weight: 600;
  color: #333;
}

.permissions-title {
  display: flex;
  align-items: center;
  margin-top: 1rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #555;
}

.permissions-list {
  margin-top: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 0.25rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
}

.permission-item {
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
}

.permission-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.permission-name {
  display: block;
  font-weight: 500;
  color: #343a40;
  margin-bottom: 0.25rem;
}

.permission-description {
  display: block;
  font-size: 0.85rem;
  color: #6c757d;
}

.permissions-summary {
  margin-top: 0.75rem;
}

.permission-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.permission-chip {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: #e7f5ff;
  color: #0d6efd;
  border-radius: 1rem;
  font-size: 0.85rem;
}

.more-indicator {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: #f8f9fa;
  color: #6c757d;
  border-radius: 1rem;
  font-size: 0.85rem;
}

.no-permissions-message {
  margin-top: 1rem;
  color: #6c757d;
  font-style: italic;
}
</style> 